import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Activity, FileText, Info, MessageSquare } from 'lucide-react';
import { cn } from '@freelancer-os/ui/src/lib/utils';
import { useClientDetail } from '../../hooks/useClients';
import { ClientHeader } from './components/ClientHeader';
import { InvoicesTab } from './components/InvoicesTab';
import { NotesTab } from './components/NotesTab';
import { ActivityTab } from './components/ActivityTab';
import { InfoTab } from './components/InfoTab';

const TAB_IDS = ['invoices', 'notes', 'activity', 'info'] as const;
const TAB_ICONS = {
  invoices: FileText,
  notes: MessageSquare,
  activity: Activity,
  info: Info,
} as const;

type TabId = (typeof TAB_IDS)[number];

function formatVNDCompact(amount: number): string {
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(amount % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (amount >= 1_000) {
    return `${(amount / 1_000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('vi-VN').format(amount);
}

export function ClientDetailPage() {
  const { t } = useTranslation('clients');
  const { id } = useParams();
  const clientId = id ?? null;
  const [activeTab, setActiveTab] = useState<TabId>('invoices');

  const { data: bundle, isLoading, isError } = useClientDetail(clientId);

  if (isLoading) {
    return (
      <div className="p-8 max-w-5xl mx-auto w-full">
        <p className="text-sm text-surface-500">{t('detail.loading')}</p>
      </div>
    );
  }

  if (isError || !bundle) {
    return (
      <div className="p-8 max-w-5xl mx-auto w-full">
        <h1 className="text-xl font-bold mb-4">{t('detail.notFound')}</h1>
      </div>
    );
  }

  const { client, invoices, notes, activities, stats } = bundle;

  const counts = {
    invoices: invoices.length,
    notes: notes.length,
  };

  return (
    <div className="p-8 max-w-5xl mx-auto w-full space-y-6">
      <ClientHeader client={client} />

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label={t('detail.stats.invoiceCount')} value={String(stats.totalInvoiceCount)} />
        <StatCard label={t('detail.stats.revenue')} value={formatVNDCompact(stats.totalRevenue)} />
        <StatCard
          label={t('detail.stats.unpaid')}
          value={formatVNDCompact(stats.totalUnpaid)}
          tone={stats.totalUnpaid > 0 ? 'amber' : 'neutral'}
        />
        <StatCard
          label={t('detail.stats.overdue')}
          value={String(stats.overdueCount)}
          tone={stats.overdueCount > 0 ? 'red' : 'neutral'}
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-200 w-full">
        {TAB_IDS.map((tabId) => {
          const isActive = activeTab === tabId;
          const Icon = TAB_ICONS[tabId];
          const count =
            tabId === 'invoices' ? counts.invoices : tabId === 'notes' ? counts.notes : null;

          return (
            <button
              key={tabId}
              type="button"
              onClick={() => setActiveTab(tabId)}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                isActive
                  ? 'border-brand-500 text-brand-700'
                  : 'border-transparent text-surface-500 hover:text-surface-700 hover:border-surface-300',
              )}
            >
              <Icon className="w-4 h-4" strokeWidth={1.8} />
              {t(`detail.tabs.${tabId}`)}
              {count !== null && (
                <span className="text-xs text-surface-400 font-normal">({count})</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'invoices' && <InvoicesTab invoices={invoices} clientId={client.id} />}
        {activeTab === 'notes' && <NotesTab clientId={client.id} notes={notes} />}
        {activeTab === 'activity' && (
          <ActivityTab clientId={client.id} initialActivities={activities} />
        )}
        {activeTab === 'info' && <InfoTab client={client} />}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'amber' | 'red';
}) {
  const valueClass =
    tone === 'amber' ? 'text-amber-600' : tone === 'red' ? 'text-red-600' : 'text-surface-900';
  return (
    <div className="border border-surface-200 rounded-md p-4 bg-surface-0">
      <div className={cn('text-2xl font-bold', valueClass)}>{value}</div>
      <div className="text-xs text-surface-500 mt-1">{label}</div>
    </div>
  );
}
